import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PortailFacturationError,
  STATUTS_ABONNEMENT,
  abonnementStore,
  activerCadeau,
  apercuChangement,
  changerFormule,
  chargerFacturation,
  chargerFormules,
  compter,
  estVivant,
  euros,
  lienGestionStore,
  ouvrirPaiement,
  peutGerer,
  phraseEcheance,
  quantiteValide,
  reprendre,
  resilier,
  seModifie,
  verifierCode,
} from './billing.js';
import { SectionPacks } from './Packs.jsx';

const UNITE_DOSSIERS = { singulier: 'dossier', pluriel: 'dossiers' };

/** Nom affiché d'un produit de la suite (abonnement qui en inclut un autre). */
const NOMS_PRODUITS = { APP: 'SEmplyApp', PREVI: 'SEmplyPrevi', COMPTA: 'SEmplyCompta', IMMO: 'SEmplyImmo' };
const STATUTS_OUVRANTS = ['ACTIVE', 'TRIALING', 'PAST_DUE'];

const message = (e, repli) => (e instanceof PortailFacturationError || e instanceof Error ? e.message : repli);

/**
 * « Mon abonnement » — le même écran dans les quatre produits.
 *
 * Il remplace quatre pages qui avaient dérivé : grille de cartes sans code
 * promo dans App, quantité seule dans Compta et Previ, choix de formule dans
 * Immo ; message de résiliation différent partout (Previ annonçait même des
 * dossiers « plus accessibles », à l'inverse de la règle) ; contrôle du rôle
 * présent ici, absent là.
 *
 * Deux chemins, et c'est le point clé :
 *  - aucun abonnement payant en cours → SOUSCRIRE (paiement Stripe embarqué) ;
 *  - un abonnement en cours → CHANGER (aperçu du prorata, puis modification de
 *    l'abonnement existant). Plus jamais de « nouveau paiement » qui créait un
 *    second abonnement Stripe à côté du premier.
 *
 * Un abonnement pris dans l'appli mobile (App Store, Google Play — 02/11/2026)
 * ne se gère que sur le store : l'écran le montre, dit où le modifier, et ne
 * propose ni changement, ni reprise, ni résiliation (le portail les refuse).
 *
 * Le paiement embarqué est rendu par l'appelant (`renderCheckout`) : les
 * produits ont déjà `@stripe/react-stripe-js`, ce paquet n'en dépend pas.
 */
export { SESSION_PORTAIL } from './billing.js';

export function Abonnement({
  apiUrl,
  getToken,
  product,
  nomProduit,
  description,
  unite = UNITE_DOSSIERS,
  avecQuantite = true,
  maxQuantite = 100,
  returnUrl,
  renderCheckout,
  onChange,
  bandeau,
  interceptSouscription,
  souscriptionFermee = false,
  spaceId = null,
  children,
}) {
  const [facturation, setFacturation] = useState(null);
  const [formules, setFormules] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState(null);

  const [planId, setPlanId] = useState(null);
  const [quantiteSaisie, setQuantiteSaisie] = useState('1');
  const [code, setCode] = useState('');
  const [codeApercu, setCodeApercu] = useState(null);
  const [codeErreur, setCodeErreur] = useState(null);

  const [apercu, setApercu] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [confirmerResiliation, setConfirmerResiliation] = useState(false);
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [etat, grille] = await Promise.all([
        chargerFacturation(apiUrl, getToken, spaceId),
        chargerFormules(apiUrl, getToken),
      ]);
      setFacturation(etat);
      setFormules((grille ?? []).filter((f) => f.product === product && f.stripePriceId));
      setErreurChargement(null);
    } catch (e) {
      setErreurChargement(
        e instanceof PortailFacturationError ? `${e.message} (${e.status})` : 'Impossible de joindre le portail.',
      );
    } finally {
      setChargement(false);
    }
  }, [apiUrl, getToken, product, spaceId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const abonnement = facturation?.subscriptions?.find((s) => s.product === product) ?? null;
  const gestionnaire = peutGerer(facturation?.role);
  const modification = seModifie(abonnement);
  // Pris dans l'appli mobile et en cours : le store seul le gère.
  const store = abonnementStore(abonnement);

  // Présélection : la formule en cours, sinon la première de la grille ; la
  // quantité en cours, sinon 1.
  useEffect(() => {
    if (formules.length === 0) return;
    setPlanId((courant) => {
      if (courant && formules.some((f) => f.id === courant)) return courant;
      const actuelle = formules.find((f) => f.id === (abonnement?.planId ?? abonnement?.plan?.id));
      return (actuelle ?? formules[0]).id;
    });
  }, [formules, abonnement?.planId, abonnement?.plan?.id]);
  useEffect(() => {
    if (abonnement?.quantity && estVivant(abonnement)) setQuantiteSaisie(String(abonnement.quantity));
  }, [abonnement?.quantity, abonnement?.status]);

  const formule = useMemo(() => formules.find((f) => f.id === planId) ?? null, [formules, planId]);
  const quantite = avecQuantite ? Number(quantiteSaisie) : 1;
  const quantiteOk = !avecQuantite || quantiteValide(quantiteSaisie, maxQuantite);
  const inchange =
    modification &&
    formule &&
    (abonnement.planId ?? abonnement.plan?.id) === formule.id &&
    abonnement.quantity === quantite;

  // Toute modification de la sélection périme l'aperçu affiché.
  useEffect(() => {
    setApercu(null);
  }, [planId, quantiteSaisie]);

  const apresChangement = async (texte) => {
    setSucces(texte);
    // Les droits voyagent dans le jeton : l'appelant le renouvelle, sans quoi
    // les menus resteraient ceux de l'ancienne formule jusqu'au prochain rafraîchissement.
    await onChange?.();
    await charger();
  };

  async function agir(nom, action, repli) {
    setEnCours(nom);
    setErreur(null);
    setSucces(null);
    try {
      await action();
    } catch (e) {
      setErreur(message(e, repli));
    } finally {
      setEnCours(null);
    }
  }

  const codeRemise = codeApercu?.kind === 'DISCOUNT' ? codeApercu.code : null;

  const souscrire = () =>
    agir(
      'paiement',
      async () => {
        if (!formule || !quantiteOk) return;
        // Un produit peut exiger une étape avant de payer (App : créer le
        // dossier d'entreprise). Il la prend en charge et dit « stop ».
        if (interceptSouscription?.(formule, quantite)) return;
        const session = await ouvrirPaiement(apiUrl, getToken, {
          priceId: formule.stripePriceId,
          quantity: quantite,
          returnUrl,
          code: codeRemise,
        }, spaceId);
        // L'espace a déjà son abonnement (29/10/2026) : le produit en devient
        // une ligne, au prorata jusqu'à l'échéance commune — un aperçu à
        // confirmer, pas un second paiement.
        if (session.ajout) setApercu(session.apercu);
        else setClientSecret(session.clientSecret);
      },
      'Impossible d’ouvrir le paiement.',
    );

  const voirChangement = () =>
    agir(
      'apercu',
      async () => {
        if (!formule || !quantiteOk) return;
        setApercu(
          await apercuChangement(apiUrl, getToken, {
            priceId: formule.stripePriceId,
            quantity: quantite,
            code: codeRemise,
          }, spaceId),
        );
      },
      'Impossible de calculer le changement.',
    );

  const confirmerChangement = () =>
    agir(
      'changement',
      async () => {
        await changerFormule(apiUrl, getToken, {
          priceId: formule.stripePriceId,
          quantity: quantite,
          code: codeRemise,
          prorationDate: apercu?.prorationDate,
        }, spaceId);
        setApercu(null);
        setCode('');
        setCodeApercu(null);
        await apresChangement('Votre abonnement a été modifié.');
      },
      'Le changement a échoué.',
    );

  const confirmerResilier = () =>
    agir(
      'resiliation',
      async () => {
        await resilier(apiUrl, getToken, product, spaceId);
        setConfirmerResiliation(false);
        await apresChangement('Résiliation enregistrée : votre accès court jusqu’à l’échéance.');
      },
      'La résiliation a échoué.',
    );

  const reprendreAbonnement = () =>
    agir(
      'reprise',
      async () => {
        await reprendre(apiUrl, getToken, product, spaceId);
        await apresChangement('Votre abonnement continue : la résiliation est annulée.');
      },
      'Impossible de reprendre l’abonnement.',
    );

  const verifier = () =>
    agir(
      'code',
      async () => {
        setCodeApercu(null);
        setCodeErreur(null);
        if (!code.trim() || !planId) return;
        try {
          setCodeApercu(await verifierCode(apiUrl, getToken, code.trim(), planId, spaceId));
        } catch (e) {
          setCodeErreur(message(e, 'Impossible de vérifier ce code.'));
        }
      },
      'Impossible de vérifier ce code.',
    );

  const activer = () =>
    agir(
      'cadeau',
      async () => {
        await activerCadeau(apiUrl, getToken, code.trim(), spaceId);
        setCode('');
        setCodeApercu(null);
        await apresChangement('Votre accès offert est ouvert.');
      },
      'Impossible d’activer ce code.',
    );

  const fermerPaiement = useCallback(() => {
    setClientSecret(null);
    void charger();
  }, [charger]);

  useEffect(() => {
    if (!clientSecret) return undefined;
    const surTouche = (e) => {
      if (e.key === 'Escape') fermerPaiement();
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [clientSecret, fermerPaiement]);

  const titre = `Abonnement ${nomProduit}`;

  if (chargement && !facturation) {
    return (
      <section className="abonnement">
        <h1>{titre}</h1>
        <p role="status">Chargement…</p>
      </section>
    );
  }

  if (erreurChargement && !facturation) {
    return (
      <section className="abonnement">
        <h1>{titre}</h1>
        <p className="alert alert-error" role="alert">
          {erreurChargement}
        </p>
        <p className="abonnement__aide">
          Ce n’est pas une absence d’offre : vérifiez que votre compte est bien rattaché à un espace,
          puis rechargez la page.
        </p>
      </section>
    );
  }

  const statut = abonnement ? STATUTS_ABONNEMENT[abonnement.status] : null;
  const actifOuEssai = abonnement && STATUTS_OUVRANTS.includes(abonnement.status);
  // Offre groupée (07/10/2026) : le produit peut être ouvert par l'abonnement
  // d'un AUTRE produit (formule Full SEmplyApp incluant Compta et Previ). Sans
  // ce cas, l'écran affirmait « pas encore d'abonnement » à un client qui paie.
  const inclusDans = actifOuEssai
    ? null
    : (facturation?.subscriptions ?? []).find(
        (s) => s.product !== product && STATUTS_OUVRANTS.includes(s.status) && (s.plan?.includes ?? []).includes(product),
      ) ?? null;
  const accesInclus =
    !actifOuEssai &&
    (Boolean(inclusDans) || (facturation?.access?.entitlements ?? []).includes(String(product).toLowerCase()));
  const total = formule && quantiteOk ? Number(formule.priceMonthly) * quantite : null;

  return (
    <section className="abonnement">
      <header className="abonnement__entete">
        <h1>{titre}</h1>
        {description && <p className="abonnement__aide">{description}</p>}
      </header>

      {bandeau}

      {succes && (
        <p className="alert alert-success" role="status">
          {succes}
        </p>
      )}

      {/* ── Abonnement actuel ─────────────────────────────────────────── */}
      <div className="card abonnement__carte">
        <h2>Abonnement actuel</h2>
        {abonnement && abonnement.status !== 'CANCELED' ? (
          <>
            <p className="abonnement__ligne">
              <strong>{abonnement.plan?.name ?? nomProduit}</strong>
              {/* Le prix payé au store est le sien, pas celui de la grille. */}
              {store ? (
                <span>Pris dans l’appli mobile, sur {store.nom}</span>
              ) : abonnement.plan?.priceMonthly != null && (
                <span>
                  {euros(abonnement.plan.priceMonthly)}/mois
                  {avecQuantite && ` × ${abonnement.quantity}`}
                </span>
              )}
              {avecQuantite && <span>{compter(abonnement.quantity, unite)}</span>}
              <span style={{ color: statut?.color }}>{statut?.label ?? abonnement.status}</span>
            </p>
            <p className="abonnement__aide">{phraseEcheance(abonnement)}</p>
            {abonnement.status === 'PAST_DUE' &&
              (store ? (
                <p className="alert alert-warning">
                  {store.sujet} n’a pas pu prélever le renouvellement. Mettez à jour le moyen de
                  paiement de votre compte {store.compte} pour garder l’accès.
                </p>
              ) : (
                <p className="alert alert-warning">
                  Le dernier prélèvement n’a pas abouti. Mettez à jour votre moyen de paiement pour
                  garder l’accès — écrivez-nous depuis « Aide &amp; support » si besoin.
                </p>
              ))}

            {store && (
              <>
                <p className="abonnement__aide">
                  Cet abonnement se modifie ou se résilie {store.gestion}, ou en ligne avec votre
                  compte {store.compte}.
                </p>
                <a
                  className="btn btn-secondary"
                  href={lienGestionStore(abonnement)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {store.bouton}
                </a>
              </>
            )}

            {!store && gestionnaire && modification && abonnement.cancelAtPeriodEnd && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={enCours !== null}
                onClick={reprendreAbonnement}
              >
                {enCours === 'reprise' ? 'Reprise…' : 'Reprendre mon abonnement'}
              </button>
            )}

            {!store && gestionnaire && actifOuEssai && !abonnement.cancelAtPeriodEnd && (
              <div className="abonnement__resiliation">
                {!confirmerResiliation ? (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => setConfirmerResiliation(true)}
                  >
                    Résilier mon abonnement
                  </button>
                ) : (
                  <div role="alertdialog" aria-label="Confirmer la résiliation">
                    <p>
                      La résiliation prend effet à la fin de la période payée. Ensuite, vos{' '}
                      {unite.pluriel} passent en lecture seule : <strong>rien n’est supprimé</strong>,
                      et reprendre un abonnement les rouvre tels quels.
                    </p>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={enCours !== null}
                      onClick={confirmerResilier}
                    >
                      {enCours === 'resiliation' ? 'Résiliation…' : 'Confirmer la résiliation'}
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setConfirmerResiliation(false)}
                    >
                      Garder mon abonnement
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="abonnement__aide">
            {accesInclus
              ? inclusDans
                ? `${nomProduit} est inclus dans votre abonnement ${NOMS_PRODUITS[inclusDans.product] ?? inclusDans.product}${inclusDans.plan?.name ? ` (${inclusDans.plan.name})` : ''} : rien à souscrire.`
                : `${nomProduit} est inclus dans un abonnement de votre espace : rien à souscrire.`
              : abonnement?.status === 'CANCELED'
                ? `Votre abonnement ${nomProduit} est terminé. Vos ${unite.pluriel} sont conservés : souscrivez à nouveau pour les rouvrir.`
                : `Votre espace n’a pas encore d’abonnement ${nomProduit}.`}
          </p>
        )}
      </div>

      {typeof children === 'function' ? children({ abonnement, actif: Boolean(actifOuEssai || accesInclus) }) : children}

      {/* ── Souscrire / changer ───────────────────────────────────────── */}
      <div className="card abonnement__carte">
        <h2>{modification ? 'Changer de formule' : 'Souscrire'}</h2>

        {store ? (
          <p className="abonnement__aide">
            Votre abonnement a été pris dans l’appli mobile SEmply : changez de formule dans l’appli
            (Mon compte › Formules), ou {store.gestion}.
          </p>
        ) : souscriptionFermee && !modification ? (
          <p className="abonnement__aide">
            Les souscriptions à {nomProduit} ne sont pas encore ouvertes.
          </p>
        ) : !gestionnaire ? (
          <p className="alert alert-warning">
            L’abonnement est géré par le propriétaire ou un administrateur de votre espace.
            Adressez-vous à lui pour souscrire, changer de formule ou résilier.
          </p>
        ) : formules.length === 0 ? (
          <p className="abonnement__aide">
            Aucune formule {nomProduit} n’est proposée à la vente pour le moment.
          </p>
        ) : (
          <>
            {formules.length > 1 ? (
              <fieldset className="abonnement__formules">
                <legend>Formule</legend>
                {formules.map((f) => {
                  const actuelle = modification && (abonnement.planId ?? abonnement.plan?.id) === f.id;
                  return (
                    <label
                      key={f.id}
                      className={`abonnement__formule${planId === f.id ? ' is-selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`formule-${product}`}
                        value={f.id}
                        checked={planId === f.id}
                        onChange={() => {
                          setPlanId(f.id);
                          setCodeApercu(null);
                        }}
                      />
                      <span className="abonnement__formule-nom">
                        {f.name}
                        {actuelle && <span className="abonnement__badge">Formule actuelle</span>}
                      </span>
                      <span className="abonnement__formule-prix">{euros(f.priceMonthly)}/mois</span>
                      {f.description && <span className="abonnement__aide">{f.description}</span>}
                      {f.features?.length > 0 && (
                        <ul className="abonnement__inclus">
                          {f.features.map((x) => (
                            <li key={x.feature.key}>
                              {x.feature.label}
                              {x.feature.kind === 'QUOTA' && ` : ${x.limit ?? 'illimité'}`}
                            </li>
                          ))}
                        </ul>
                      )}
                    </label>
                  );
                })}
              </fieldset>
            ) : (
              <p className="abonnement__ligne">
                <strong>{formule?.name}</strong>
                <span>
                  {euros(formule?.priceMonthly)}/mois{avecQuantite && ` par ${unite.singulier}`}
                </span>
              </p>
            )}

            {avecQuantite && (
              <div className="abonnement__quantite">
                <label htmlFor={`quantite-${product}`}>Nombre de {unite.pluriel}</label>
                <input
                  id={`quantite-${product}`}
                  type="number"
                  min={1}
                  max={maxQuantite}
                  value={quantiteSaisie}
                  onChange={(e) => setQuantiteSaisie(e.target.value)}
                  aria-invalid={!quantiteOk}
                  aria-describedby={!quantiteOk ? `quantite-erreur-${product}` : undefined}
                />
                {!quantiteOk && (
                  <p className="alert alert-error" id={`quantite-erreur-${product}`} role="alert">
                    Indiquez un nombre de {unite.pluriel} entre 1 et {maxQuantite}.
                  </p>
                )}
              </div>
            )}

            {total !== null && (
              <p className="abonnement__total">
                Total : <strong>{euros(total)}/mois</strong>
              </p>
            )}

            {/* Même champ pour les deux natures de code : le client ne sait
                pas laquelle il tient. La vérification tranche. */}
            <div className="abonnement__code">
              <label htmlFor={`code-${product}`}>Vous avez un code ?</label>
              <div className="abonnement__code-saisie">
                <input
                  id={`code-${product}`}
                  type="text"
                  value={code}
                  placeholder="RENTREE2026"
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase());
                    setCodeApercu(null);
                    setCodeErreur(null);
                  }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={enCours !== null || !code.trim() || !planId}
                  onClick={verifier}
                >
                  {enCours === 'code' ? 'Vérification…' : 'Vérifier'}
                </button>
              </div>
              {codeErreur && (
                <p className="alert alert-error" role="alert">
                  {codeErreur}
                </p>
              )}
              {codeApercu && (
                <div role="status">
                  <p>
                    <strong>{codeApercu.label}</strong> — {codeApercu.resume}
                  </p>
                  {codeApercu.kind === 'GIFT' ? (
                    <>
                      <p className="abonnement__aide">
                        Aucun paiement : l’accès s’ouvre immédiatement et se ferme seul au terme.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={enCours !== null}
                        onClick={activer}
                      >
                        {enCours === 'cadeau' ? 'Activation…' : 'Activer mon accès offert'}
                      </button>
                    </>
                  ) : (
                    <p className="abonnement__aide">La remise sera appliquée à votre paiement.</p>
                  )}
                </div>
              )}
            </div>

            {modification || apercu ? (
              apercu ? (
                <div className="abonnement__apercu" role="status">
                  {typeof apercu.prorata === 'number' ? (
                    <p>
                      {apercu.prorata >= 0 ? (
                        <>
                          Prorata jusqu’à l’échéance : <strong>{euros(apercu.prorata)}</strong>, ajouté à votre
                          prochaine facture. Rien n’est prélevé aujourd’hui.
                        </>
                      ) : (
                        <>
                          Crédit de <strong>{euros(-apercu.prorata)}</strong> déduit de votre prochaine facture.
                        </>
                      )}
                    </p>
                  ) : (
                  <p>
                    {apercu.dueNow > 0 ? (
                      <>
                        À régler aujourd’hui (au prorata) : <strong>{euros(apercu.dueNow)}</strong>, sur
                        votre moyen de paiement enregistré.
                      </>
                    ) : apercu.dueNow < 0 ? (
                      <>
                        Crédit de <strong>{euros(-apercu.dueNow)}</strong> déduit de vos prochaines
                        factures.
                      </>
                    ) : (
                      <>Rien à régler aujourd’hui.</>
                    )}
                  </p>
                  )}
                  <p>
                    Ensuite : <strong>{euros(apercu.monthlyAfter)}/mois</strong>
                    {apercu.currentPeriodEnd &&
                      `, à partir du ${new Date(apercu.currentPeriodEnd).toLocaleDateString('fr-FR')}`}
                    .
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={enCours !== null}
                    onClick={confirmerChangement}
                  >
                    {enCours === 'changement' ? 'Modification…' : 'Confirmer le changement'}
                  </button>{' '}
                  <button type="button" className="btn btn-secondary" onClick={() => setApercu(null)}>
                    Annuler
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={enCours !== null || !quantiteOk || !formule || inchange}
                  onClick={voirChangement}
                >
                  {enCours === 'apercu'
                    ? 'Calcul…'
                    : inchange
                      ? 'C’est votre formule actuelle'
                      : 'Voir le montant du changement'}
                </button>
              )
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={enCours !== null || !quantiteOk || !formule}
                onClick={souscrire}
              >
                {enCours === 'paiement' ? 'Chargement…' : 'Souscrire'}
              </button>
            )}
          </>
        )}
      </div>

      {erreur && (
        <p className="alert alert-error" role="alert">
          {erreur}
        </p>
      )}

      {/* Packs (04/10/2026) : à l'unité, avec ou sans abonnement. */}
      <SectionPacks
        apiUrl={apiUrl}
        getToken={getToken}
        product={product}
        spaceId={spaceId}
        onPaiement={setClientSecret}
        onChange={onChange}
        ventesFermees={souscriptionFermee}
      />

      {clientSecret && (
        <div className="abonnement__voile">
          <div
            className="abonnement__paiement"
            role="dialog"
            aria-modal="true"
            aria-label="Paiement"
          >
            <button
              type="button"
              className="btn btn-secondary btn-small abonnement__fermer"
              onClick={fermerPaiement}
              aria-label="Fermer le paiement"
              autoFocus
            >
              <span aria-hidden="true">✕</span>
            </button>
            {renderCheckout?.(clientSecret)}
          </div>
        </div>
      )}
    </section>
  );
}
